import { html } from "htm/preact";
import { useEffect, useMemo, useState } from "preact/hooks";
import { getClient } from "../client.js";
import { NotJoined } from "../components/NotJoined.js";
import { loadCourse } from "../courseData.js";
import { computeLiveRoundResults } from "../../../shared/leaderboard.js";
import { explainRound, type BreakdownRow, type HoleLine } from "../../../shared/explain.js";
import { scoreTone } from "../util.js";
import { Icon } from "../components/Icon.js";
import type { Course } from "../../../shared/types.js";

/** The summary rows: gross → strokes → net → placing → points. */
function Summary({ rows }: { rows: BreakdownRow[] }) {
  return html`
    <div class="calc">
      ${rows.map(
        (r, i) => html`
          <div class=${`calc-row ${r.emphasis ? "calc-total" : ""}`} key=${i}>
            <span class="calc-label">
              ${r.label}
              ${r.note && html`<small>${r.note}</small>`}
            </span>
            <span class="calc-value">${r.value}</span>
          </div>
        `,
      )}
    </div>
  `;
}

function HoleTable({ holes, anyTags }: { holes: HoleLine[]; anyTags: boolean }) {
  return html`
    <div class="table-scroll">
      <table class="scorecard-table">
        <thead>
          <tr>
            <th>Hole</th>
            <th>Par</th>
            <th>SI</th>
            <th>Gross</th>
            <th>Strokes</th>
            <th>Net</th>
            ${anyTags && html`<th>Result</th>`}
          </tr>
        </thead>
        <tbody>
          ${holes.map(
            (h) => html`
              <tr key=${h.hole} class=${h.gross == null ? "row-empty" : ""}>
                <td>${h.hole}</td>
                <td>${h.par}</td>
                <td>${h.strokeIndex}</td>
                <td class=${scoreTone(h.gross, h.par)}>${h.gross ?? "–"}</td>
                <td>${h.received > 0 ? `−${h.received}` : "–"}</td>
                <td>${h.net ?? "–"}</td>
                ${anyTags && html`<td class="cell-note">${h.tag ?? ""}</td>`}
              </tr>
            `,
          )}
        </tbody>
      </table>
    </div>
  `;
}

/**
 * Why one player scored what they did in one round.
 *
 * A route rather than a modal, matching RoundRules: the back button then works, and
 * neither list it was opened from has to change shape.
 */
export function PlayerRound({
  code,
  roundId,
  playerId,
}: {
  code: string;
  roundId: string;
  playerId: string;
}) {
  const client = getClient(code);
  const snapshot = client?.snapshot.value ?? null;
  const round = snapshot?.tournament.rounds.find((r) => r.id === roundId) ?? null;
  const [course, setCourse] = useState<Course | null>(null);

  useEffect(() => {
    if (round?.courseId) loadCourse(round.courseId).then(setCourse);
  }, [round?.courseId]);

  const explanation = useMemo(() => {
    if (!snapshot || !round) return null;
    const courses = new Map<string, Course>();
    if (course) courses.set(course.id, course);
    const results = computeLiveRoundResults(
      snapshot.tournament,
      courses,
      snapshot.scores,
      snapshot.perHoleData,
    );
    const result = results.find((r) => r.roundId === round.id);
    if (!result) return null;
    return explainRound(
      round,
      course ?? undefined,
      snapshot.tournament.players,
      snapshot.scores,
      snapshot.perHoleData,
      result,
      playerId,
    );
  }, [snapshot, round, course, playerId]);

  if (!client) return html`<${NotJoined} code=${code} />`;
  if (!snapshot || !round) return html`<p class="muted">Loading…</p>`;

  const name = snapshot.tournament.players.find((p) => p.id === playerId)?.name ?? playerId;
  if (!explanation) return html`<p class="muted">Loading…</p>`;

  const anyTags = explanation.holes.some((h) => h.tag);

  return html`
    <p class="back-link">
      <a href=${`#/t/${code}/leaderboard`}><${Icon} name="left" size=${16} /> Standings</a>
    </p>

    <h1 class="mb-0">${name}</h1>
    <p class="course-line">
      ${explanation.roundName} · ${explanation.formatName}
      ${explanation.courseName ? ` · ${explanation.courseName}` : ""}
      ${explanation.teeName ? ` (${explanation.teeName})` : ""}
    </p>

    ${explanation.provisional &&
    html`<p class="notice notice-ok">
      <span class="dot"></span> This round is still open, so these points aren't in the standings
      yet.
    </p>`}

    <div class="panel">
      <span class="eyebrow">How this round scored</span>
      <${Summary} rows=${explanation.summary} />
    </div>

    ${explanation.handicap &&
    explanation.handicap.playing > 0 &&
    html`
      <div class="panel">
        <span class="eyebrow">Handicap</span>
        <${Summary}
          rows=${[
            { label: "Handicap index", value: String(explanation.handicap.index) },
            {
              label: "Course handicap",
              value: String(explanation.handicap.course),
              note: "index adjusted for the tee's slope",
            },
            {
              label: "Playing handicap",
              value: String(explanation.handicap.playing),
              note: `${explanation.handicap.allowancePercent}% allowance${
                explanation.handicap.halvedForNineHoles ? ", halved for a 9-hole round" : ""
              }`,
            },
          ]}
        />
        <p class="field-help mb-0">
          Those strokes go on the hardest holes first, by stroke index — that's the
          <strong>Strokes</strong> column below.
        </p>
      </div>
    `}

    ${explanation.holes.length > 0 &&
    html`
      <div class="panel">
        <span class="eyebrow">Hole by hole</span>
        <${HoleTable} holes=${explanation.holes} anyTags=${anyTags} />
        <p class="field-help mb-0">
          <a href=${`#/t/${code}/round/${roundId}/holes`}>See every player hole by hole</a>
        </p>
      </div>
    `}

    <div class="panel">
      <span class="eyebrow">The rules being applied</span>
      <ul class="rule-list">
        ${explanation.rules.map((line, i) => html`<li key=${i}>${line}</li>`)}
      </ul>
    </div>
  `;
}
